import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IDirData, MeanDir, VGPData } from '../../utils/GlobalTypes';
import { StatisitcsInterpretationFromDIR } from '../../utils/GlobalTypes';
import { Reference, StatisticsModeDIR } from '../../utils/graphs/types';

interface IInitialState {
  vgpData: VGPData | null;
  vgpMean: MeanDir | null;
  reference: Reference;
  selectedDirectionsIDs: Array<number> | null;
  hiddenDirectionsIDs: Array<number>;
  reversedDirectionsIDs: Array<number>;
  statisticsMode: StatisticsModeDIR;
  isCommentsInputVisible: boolean;
  currentInterpretation: StatisitcsInterpretationFromDIR | null;
  currentFileInterpretations: Array<StatisitcsInterpretationFromDIR>;
  allInterpretations: Array<StatisitcsInterpretationFromDIR>;
  outputFilename: string;
  showSelectionInput: boolean;
  showVGPMean: boolean;
  labelModeIsNumeric: boolean;
  centeredByMean: boolean;
  cutoffEnabled: boolean;
  cutoffAngle: number;
}

const initialState: IInitialState = {
  vgpData: null,
  vgpMean: null,
  reference: 'geographic',
  selectedDirectionsIDs: null,
  hiddenDirectionsIDs: [],
  reversedDirectionsIDs: [],
  statisticsMode: null,
  isCommentsInputVisible: true,
  currentInterpretation: null,
  currentFileInterpretations: [],
  allInterpretations: [],
  outputFilename: '',
  showSelectionInput: false,
  showVGPMean: false,
  labelModeIsNumeric: false,
  centeredByMean: false,
  cutoffEnabled: false,
  cutoffAngle: 45,
};

const dirPage = createSlice({
  name: 'dirPage',
  initialState,
  reducers: {
    setReference(state, action) {
      state.reference = action.payload;
      localStorage.setItem('dirPage_reference', JSON.stringify(state.reference));
    },
    setSelectedDirectionsIDs(state, action) {
      state.selectedDirectionsIDs = action.payload;
    },
    setHiddenDirectionsIDs(state, action: { payload: Array<number> }) {
      state.hiddenDirectionsIDs = action.payload;
    },
    addHiddenDirectionsIDs(state, action: { payload: Array<number> }) {
      const updatedHiddenDirectionsIDs = [
        ...new Set([...state.hiddenDirectionsIDs, ...action.payload]),
      ];
      state.hiddenDirectionsIDs = updatedHiddenDirectionsIDs;
    },
    removeHiddenDirectionsIDs(state, action: { payload: Array<number> }) {
      // Remove only the ids in the payload — directions hidden by other means
      // (e.g. the eye icon) must stay hidden. (Previously this unioned the
      // payload with the current set and filtered all of them out, so it always
      // cleared every hidden direction regardless of the payload.)
      const idsToRemove = new Set(action.payload);
      state.hiddenDirectionsIDs = state.hiddenDirectionsIDs.filter((id) => !idsToRemove.has(id));
    },
    setReversedDirectionsIDs(state, action: { payload: Array<number> }) {
      state.reversedDirectionsIDs = action.payload;
    },
    addReversedDirectionsIDs(state, action: { payload: Array<number> }) {
      const updatedReversedDirectionsIDs = [
        ...new Set([...state.reversedDirectionsIDs, ...action.payload]),
      ];
      state.reversedDirectionsIDs = updatedReversedDirectionsIDs;
    },
    showSelectionInput(state, action) {
      state.showSelectionInput = action.payload;
    },
    setStatisticsMode(state, action) {
      state.statisticsMode = action.payload;
    },
    // Center-by-mean / cutoff view-state (shared between the DIR stereonet,
    // the directions table and the export menu)
    setCenteredByMean(state, action: { payload: boolean }) {
      state.centeredByMean = action.payload;
    },
    setCutoffEnabled(state, action: { payload: boolean }) {
      state.cutoffEnabled = action.payload;
    },
    setCutoffAngle(state, action: { payload: number }) {
      state.cutoffAngle = action.payload;
    },
    toggleCommentsInput(state) {
      state.isCommentsInputVisible = !state.isCommentsInputVisible;
      localStorage.setItem(
        'dirPage_isCommentsInputVisible',
        JSON.stringify(state.isCommentsInputVisible),
      );
    },
    setCommentsInput(state, action: { payload: boolean }) {
      state.isCommentsInputVisible = action.payload;
      localStorage.setItem(
        'dirPage_isCommentsInputVisible',
        JSON.stringify(state.isCommentsInputVisible),
      );
    },
    // Label mode (numeric vs filename)
    toggleLabelMode(state) {
      state.labelModeIsNumeric = !state.labelModeIsNumeric;
      localStorage.setItem('dirPage_isNumericLabel', JSON.stringify(state.labelModeIsNumeric));
    },
    setLabelMode(state, action: { payload: boolean }) {
      state.labelModeIsNumeric = action.payload;
      localStorage.setItem('dirPage_isNumericLabel', JSON.stringify(state.labelModeIsNumeric));
    },
    // VGP
    toggleShowVGPMean(state) {
      state.showVGPMean = !state.showVGPMean;
    },
    setVGPData(state, action) {
      state.vgpData = action.payload;
    },
    setVGPMean(state, action) {
      state.vgpMean = action.payload;
    },
    // interpretations manipulations below
    addInterpretation(state, action) {
      state.currentInterpretation = action.payload?.interpretation;
      state.currentFileInterpretations.push(action.payload?.interpretation);
      state.allInterpretations.push(action.payload?.interpretation);
      state.selectedDirectionsIDs = null;
      localStorage.setItem('dirPage_allInterpretations', JSON.stringify(state.allInterpretations));
      localStorage.setItem(
        'dirPage_currentInterpretation',
        JSON.stringify(state.currentInterpretation?.uuid || ''),
      );
    },
    deleteInterpretation(state, action) {
      const interpretationUUID = action.payload as string;
      const updatedInterpretations = state.allInterpretations.filter(
        (interpretation) => interpretation.uuid !== interpretationUUID,
      );
      state.allInterpretations = updatedInterpretations;
      localStorage.setItem('dirPage_allInterpretations', JSON.stringify(state.allInterpretations));
    },
    deleteInterepretationByParentFile(state, action) {
      const parentFile = action.payload;
      const updatedInterpretations = state.allInterpretations.filter(
        (interpretation) => interpretation.parentFile !== parentFile,
      );
      state.allInterpretations = updatedInterpretations;
      localStorage.setItem('dirPage_allInterpretations', JSON.stringify(state.allInterpretations));
    },
    setAllInterpretations(state, action) {
      state.allInterpretations = action.payload;
      localStorage.setItem('dirPage_allInterpretations', JSON.stringify(state.allInterpretations));
    },
    deleteAllInterpretations(state) {
      state.allInterpretations = [];
      state.currentFileInterpretations = [];
      state.currentInterpretation = null;
      localStorage.setItem('dirPage_allInterpretations', JSON.stringify(state.allInterpretations));
      localStorage.setItem('dirPage_currentInterpretation', JSON.stringify(''));
    },
    updateCurrentFileInterpretations(state, action: PayloadAction<string>) {
      const filename = action.payload;
      state.currentFileInterpretations = state.allInterpretations.filter(
        (interpretation) => interpretation.parentFile === filename,
      );
    },
    setLastInterpretationAsCurrent(state) {
      if (!state.currentFileInterpretations.length) {
        state.currentInterpretation = null;
        return;
      }
      state.currentInterpretation =
        state.currentFileInterpretations[state.currentFileInterpretations.length - 1];
      localStorage.setItem(
        'dirPage_currentInterpretation',
        JSON.stringify(state.currentInterpretation.uuid),
      );
    },
    setCurrentInterpretationByUUID(state, action: PayloadAction<{ uuid: string }>) {
      const { uuid } = action.payload;
      const interpretationToSet = state.allInterpretations.find(
        (interpretation) => interpretation.uuid === uuid,
      );
      if (!interpretationToSet) {
        return;
      }
      state.currentInterpretation = interpretationToSet;
      localStorage.setItem(
        'dirPage_currentInterpretation',
        JSON.stringify(state.currentInterpretation.uuid),
      );
    },
    setCurrentInterpretationByLabel(state, action: PayloadAction<{ label: string }>) {
      const { label } = action.payload;
      const interpretationToSet = state.allInterpretations.find(
        (interpretation) => interpretation.label === label,
      );
      if (!interpretationToSet) {
        return;
      }
      state.currentInterpretation = interpretationToSet;
      localStorage.setItem(
        'dirPage_currentInterpretation',
        JSON.stringify(state.currentInterpretation.uuid),
      );
    },
    setNextOrPrevInterpretationAsCurrent(
      state,
      action: PayloadAction<{ changeDirection: 'up' | 'down' }>,
    ) {
      if (!state.currentFileInterpretations.length || !state.currentInterpretation) {
        state.currentInterpretation = null;
        return;
      }
      const currentInterpretationIndex = state.currentFileInterpretations.findIndex(
        (interpretation) => interpretation.uuid === state.currentInterpretation?.uuid,
      );

      const { changeDirection } = action.payload;

      const toNext = changeDirection === 'up' ? 1 : -1;

      const totalInterpretations = state.currentFileInterpretations.length;
      let nextInterpretationIndex = currentInterpretationIndex + toNext;

      if (nextInterpretationIndex < 0) {
        nextInterpretationIndex = totalInterpretations - 1;
      } else if (nextInterpretationIndex >= totalInterpretations) {
        nextInterpretationIndex = 0;
      }

      const nextInterpretation = state.currentFileInterpretations[nextInterpretationIndex];
      state.currentInterpretation = nextInterpretation;
      localStorage.setItem(
        'dirPage_currentInterpretation',
        JSON.stringify(state.currentInterpretation.uuid),
      );
    },
    setOutputFilename(state, action) {
      state.outputFilename = action.payload;
    },
  },
  extraReducers: (builder) => {},
});

export const {
  setVGPData,
  setVGPMean,
  setReference,
  setSelectedDirectionsIDs,
  setHiddenDirectionsIDs,
  addHiddenDirectionsIDs,
  removeHiddenDirectionsIDs,
  setReversedDirectionsIDs,
  addReversedDirectionsIDs,
  setStatisticsMode,
  setCenteredByMean,
  setCutoffEnabled,
  setCutoffAngle,
  toggleCommentsInput,
  setCommentsInput,
  showSelectionInput,
  addInterpretation,
  deleteInterpretation,
  setAllInterpretations,
  deleteAllInterpretations,
  updateCurrentFileInterpretations,
  setLastInterpretationAsCurrent,
  setCurrentInterpretationByUUID,
  setCurrentInterpretationByLabel,
  setNextOrPrevInterpretationAsCurrent,
  setOutputFilename,
  toggleShowVGPMean,
  deleteInterepretationByParentFile,
  toggleLabelMode,
  setLabelMode,
} = dirPage.actions;

const dirPageReducer = dirPage.reducer;
export default dirPageReducer;
